import type { UtilsApi } from '../../types';

type DetectedTable = {
  startLine: number;
  endLine: number;
  reason?: string;
  preview?: string[];
};

export type DetectTablesResponse = {
  tables: DetectedTable[];
};

export type TableExtractOption = {
  value: string;
  row: number;
  col: number;
  anchor?: string;
  confidence?: number;
  repeating: boolean;
};

export type TableExtractResponse = Record<string, { options: TableExtractOption[] }>;

export function splitLines(textContent: string): string[] {
  return textContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
}

export async function detectTablesFromFullText(args: {
  textContent: string;
  extractWithLLM: UtilsApi['extractWithLLM'];
  opts?: { signal?: AbortSignal };
}): Promise<DetectTablesResponse> {
  const { textContent, extractWithLLM, opts } = args;

  if (opts?.signal?.aborted) throw new Error('Table detection aborted');

  const response = await extractWithLLM(textContent, 'Table_Detection_Declare_Items');
  if (response.isErr()) throw new Error(response.error.message);

  const raw = await response.value.text;
  const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const cleaned = fenceMatch ? fenceMatch[1].trim() : raw.trim();

  const parsed = JSON.parse(cleaned);

  return parsed;
}

export async function extractFieldsFromTableWithLLM(args: {
  tableLines: string[];
  promptName: string;
  extractWithLLM: UtilsApi['extractWithLLM'];
  retryInstruction?: string;
  fieldsSection?: string;
  signal?: AbortSignal;
}): Promise<TableExtractResponse> {
  const { tableLines, promptName, extractWithLLM, retryInstruction, fieldsSection, signal } = args;

  if (signal?.aborted) throw new Error('Table extraction aborted');

  const preamble = [fieldsSection, retryInstruction].filter(Boolean).join('\n');
  const content =
    (preamble ? `${preamble}\n\nTABLE TEXT FOLLOWS:\n` : '') + tableLines.join('\n');

  const response = await extractWithLLM(content, promptName);
  if (response.isErr()) throw new Error(response.error.message);

  let text = (await response.value.text).trim();

  if (text.startsWith('[')) {
    try {
      const messages = JSON.parse(text) as Array<{ role: string; content?: string }>;
      const lastWithContent = [...messages].reverse().find((m) => m.content);
      if (lastWithContent?.content) text = lastWithContent.content.trim();
    } catch {
    }
  }

  const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const cleaned = fenceMatch ? fenceMatch[1].trim() : text.trim();

  if (!cleaned.startsWith('{')) {
    return {} as TableExtractResponse;
  }

  return JSON.parse(cleaned) as TableExtractResponse;
}
